import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Box,
  Button,
  Divider,
  FormControl,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import formatCentsToUSD from "utils/money/formatCentsToUSD";
import { AllocationPreview, BillingMode } from "./quickbooksSyncUtils";

type Props = {
  allocationPreview: AllocationPreview;
  billingMode: BillingMode;
  fixedAmountValue: string;
  percentageValue: string;
  resetSyncAmountOverrides: () => void;
  selectedJobCount: number;
  setBillingMode: (value: BillingMode) => void;
  setFixedAmountValue: (value: string) => void;
  setPercentageValue: (value: string) => void;
  syncAmountOverrideCount: number;
};

export default function QuickbooksSyncAmountSummary({
  allocationPreview,
  billingMode,
  fixedAmountValue,
  percentageValue,
  resetSyncAmountOverrides,
  selectedJobCount,
  setBillingMode,
  setFixedAmountValue,
  setPercentageValue,
  syncAmountOverrideCount,
}: Props) {
  const allocationHelp =
    billingMode === "percentage"
      ? `Each unedited Sync Amount is ${percentageValue || 0}% of its Item Amount.`
      : billingMode === "fixed_amount"
      ? "The fixed amount is proportionally allocated across unedited lines."
      : null;

  return (
    <Paper
      variant="outlined"
      sx={{
        position: { lg: "sticky" },
        top: { lg: 0 },
        overflow: "hidden",
        borderColor: "rgba(30, 64, 175, 0.2)",
      }}
    >
      <Box sx={{ px: 1.5, py: 1.25, bgcolor: "rgba(30, 64, 175, 0.05)" }}>
        <Typography variant="overline" color="primary.main" fontWeight={700} lineHeight={1}>
          Sync Summary
        </Typography>
        <Typography variant="subtitle2" sx={{ mt: 0.35 }}>
          {selectedJobCount} {selectedJobCount === 1 ? "job" : "jobs"} included
        </Typography>
      </Box>

      <Stack spacing={1.25} sx={{ p: 1.5 }}>
        <Box>
          <Typography variant="subtitle2" mb={0.65}>
            Sync Amount Options
          </Typography>
          <FormControl fullWidth size="small">
            <Select
              value={billingMode}
              onChange={(e) => setBillingMode(e.target.value as BillingMode)}
            >
              <MenuItem value="full">Full Amount</MenuItem>
              <MenuItem value="percentage">Percentage</MenuItem>
              <MenuItem value="fixed_amount">Fixed Dollar Amount</MenuItem>
            </Select>
          </FormControl>
          {billingMode === "percentage" && (
            <TextField
              fullWidth
              size="small"
              label="Percentage"
              type="number"
              value={percentageValue}
              onChange={(e) => setPercentageValue(e.target.value)}
              sx={{ mt: 0.75 }}
            />
          )}
          {billingMode === "fixed_amount" && (
            <TextField
              fullWidth
              size="small"
              label="Dollar Amount"
              inputProps={{ inputMode: "decimal" }}
              value={fixedAmountValue}
              onChange={(e) => setFixedAmountValue(e.target.value)}
              sx={{ mt: 0.75 }}
            />
          )}
          {allocationHelp && (
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.25, mt: 0.75 }}>
              <Typography variant="caption" color="text.secondary">
                {allocationHelp} Edit a Sync Amount to override that line.
              </Typography>
              <Tooltip
                arrow
                placement="top"
                title="QuickBooks uses the exact Sync Amount shown for each line. Calculated amounts are rounded to cents, with any remaining penny assigned so the line totals match the selected amount."
              >
                <IconButton size="small" sx={{ p: 0.1, mt: -0.15 }}>
                  <InfoOutlinedIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
            </Box>
          )}
          {syncAmountOverrideCount > 0 && (
            <Button
              size="small"
              onClick={resetSyncAmountOverrides}
              sx={{ mt: 0.5, px: 0, minWidth: 0, textTransform: "none" }}
            >
              Reset {syncAmountOverrideCount} {syncAmountOverrideCount === 1 ? "override" : "overrides"}
            </Button>
          )}
        </Box>

        <Divider />

        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Selected Jobs Total
          </Typography>
          <Typography variant="body2" fontWeight={700} textAlign="right">
            {formatCentsToUSD(allocationPreview.selectedTotal)}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
          <Typography variant="body2" fontWeight={700}>
            Amount to Sync
          </Typography>
          <Typography variant="body1" fontWeight={700} color="primary.main" textAlign="right">
            {formatCentsToUSD(allocationPreview.billedTotal)}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
}
